import { api } from '@/services/apiClient';

/**
 * Organization Service
 *
 * Handles all organization, team member, and invitation operations
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import type {
  Organization,
  OrganizationMember,
  OrganizationInvitation,
  InviteMemberFormData,
  UpdateMemberFormData,
  OrganizationSettingsFormData,
  MemberPermissions,
} from '@/types/organization';
import { ROLE_PERMISSIONS } from '@/types/organization';

// ============================================================================
// ORGANIZATION OPERATIONS
// ============================================================================

export async function getUserOrganizations(userId: string): Promise<Organization[]> {
  // CRITICAL FIX: First verify we have a valid session before querying
  // RLS policies use auth.uid() which requires a JWT in the request
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (process.env.NODE_ENV === 'development') {
    console.log('[OrgService] Session check:', {
      hasSession: !!session,
      sessionUserId: session?.user?.id,
      requestedUserId: userId,
      match: session?.user?.id === userId,
      sessionError: sessionError?.message,
    });
  }

  // DIAGNOSTIC: If no session, RLS will block everything
  if (!session) {
    console.error('[OrgService] ❌ NO SESSION - RLS will block all rows!');
    console.error('[OrgService] This means auth.uid() will be NULL in the database');
    // Don't throw - return empty array to match RLS behavior
    return [];
  }

  if (session.user.id !== userId) {
    console.warn('[OrgService] ⚠️ Session user ID mismatch!', {
      sessionUserId: session.user.id,
      requestedUserId: userId,
    });
  }

  const { data, error } = await supabase
    .from('organization_members')
    .select(`
      organization_id,
      organizations (*)
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('[OrgService] getUserOrganizations ERROR:', error);
    throw error;
  }

  // DEBUG: Log raw data before filtering
  if (process.env.NODE_ENV === 'development') {
    console.log('[OrgService] Raw data from query:', JSON.stringify(data, null, 2));
    console.log('[OrgService] Number of rows returned:', data?.length ?? 0);

    // Additional RLS diagnostic
    if (data?.length === 0 && session) {
      console.error('[OrgService] ❌ Session exists but 0 rows returned!');
      console.error('[OrgService] Possible causes:');
      console.error('  1. RLS policy using auth.uid() but JWT not being sent');
      console.error('  2. organization_members record does not exist for this user');
      console.error('  3. RLS policy has a restrictive condition blocking access');
      console.error('  → Check Supabase Dashboard > Auth > Policies');
    }
  }

  // Filter out any null organizations (in case RLS blocks access to some)
  const orgs = data
    ?.map(item => item.organizations as any as Organization)
    .filter((org): org is Organization => org !== null && org !== undefined) || [];

  if (process.env.NODE_ENV === 'development') {
    console.log(`[OrgService] getUserOrganizations returned ${orgs.length} orgs for user ${userId}`);
    if (data?.length && orgs.length === 0) {
      console.warn('[OrgService] ⚠️ Rows exist but organizations are null - RLS blocking organizations table!');
    }
  }

  return orgs;
}

export async function getOrganization(organizationId: string): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', organizationId)
    .maybeSingle(); // Use maybeSingle to gracefully handle RLS blocking or missing org

  if (error) {
    // Log error but don't throw for common cases
    logger.error('getOrganization error:', error);
    return null;
  }
  return data;
}

export async function updateOrganization(
  organizationId: string,
  updates: OrganizationSettingsFormData
): Promise<Organization> {
  const { data, error} = await supabase
    .from('organizations')
    .update({
      name: updates.name,
      logo_url: updates.logo_url,
      settings: updates.settings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', organizationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// TEAM MEMBER OPERATIONS
// ============================================================================

export async function getOrganizationMembers(
  organizationId: string
): Promise<OrganizationMember[]> {
  // Fetch organization members (no cross-schema joins)
  const { data, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Get unique user IDs
  const userIds = [...new Set([
    ...data.map(m => m.user_id),
    ...data.map(m => m.invited_by).filter(Boolean)
  ])];

  // Fetch user profiles separately
  const { data: profiles, error: profilesError } = await supabase
    .from('user_profiles')
    .select('id, email, full_name')
    .in('id', userIds);

  if (profilesError) {
    logger.error('Failed to fetch user profiles:', profilesError);
    // Continue without user details rather than failing completely
  }

  // Create a map for quick lookup
  const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

  // Combine data
  return data.map(member => {
    const userProfile = profileMap.get(member.user_id);
    const inviterProfile = member.invited_by ? profileMap.get(member.invited_by) : null;

    return {
      ...member,
      email: userProfile?.email || 'unknown@example.com',
      full_name: userProfile?.full_name || 'Unknown User',
      invited_by_email: inviterProfile?.email || null,
    };
  }) as any;
}

export async function getCurrentUserMembership(
  organizationId: string,
  userId: string
): Promise<OrganizationMember | null> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('user_id', userId)
    .maybeSingle(); // Use maybeSingle to avoid 406 when no membership exists

  if (error) {
    // Log but don't throw - return null for any error (RLS, not found, etc.)
    logger.warn('getCurrentUserMembership error:', { organizationId, userId, error: error.message });
    return null;
  }

  return data;
}

export async function updateMember(
  memberId: string,
  updates: UpdateMemberFormData
): Promise<OrganizationMember> {
  const { data, error } = await supabase
    .from('organization_members')
    .update({
      role: updates.role,
      permissions: updates.permissions,
      client_access: updates.client_access,
    })
    .eq('id', memberId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', memberId);

  if (error) throw error;
}

// ============================================================================
// INVITATION OPERATIONS
// ============================================================================

export async function getOrganizationInvitations(
  organizationId: string
): Promise<OrganizationInvitation[]> {
  const { data, error} = await supabase
    .from('organization_invitations')
    .select('*')
    .eq('organization_id', organizationId)
    .is('accepted_at', null) // Only pending invitations
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(inv => ({
    ...inv,
    invited_by_email: 'Team Member', // Can't join with auth.users via PostgREST
    is_valid: new Date(inv.expires_at) > new Date(),
  })) as any;
}

export async function inviteMember(
  organizationId: string,
  invitedBy: string,
  formData: InviteMemberFormData
): Promise<OrganizationInvitation> {
  // Check if invitation already exists (pending and not expired)
  const { data: existingInvitation } = await supabase
    .from('organization_invitations')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('email', formData.email.toLowerCase())
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();

  if (existingInvitation) {
    throw new Error('An invitation has already been sent to this email');
  }

  // Create invitation
  const { data, error } = await supabase
    .from('organization_invitations')
    .insert({
      organization_id: organizationId,
      email: formData.email.toLowerCase(),
      role: formData.role,
      permissions: formData.permissions,
      client_access: formData.client_access,
      invited_by: invitedBy,
    })
    .select()
    .single();

  if (error) throw error;

  // TODO: Send invitation email
  await sendInvitationEmail(data);

  return data;
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('organization_invitations')
    .delete()
    .eq('id', invitationId);

  if (error) throw error;
}

export async function resendInvitation(invitationId: string): Promise<void> {
  // Update expiration date
  const { data, error } = await supabase
    .from('organization_invitations')
    .update({
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
    })
    .eq('id', invitationId)
    .select()
    .single();

  if (error) throw error;

  // TODO: Resend invitation email
  await sendInvitationEmail(data);
}

export async function acceptInvitation(token: string, userId: string): Promise<void> {
  // Get invitation
  const { data: invitation, error: fetchError } = await supabase
    .from('organization_invitations')
    .select('*')
    .eq('token', token)
    .is('accepted_at', null)
    .single();

  if (fetchError) throw new Error('Invalid or expired invitation');

  // Check expiration
  if (new Date(invitation.expires_at) < new Date()) {
    throw new Error('This invitation has expired');
  }

  // Create organization member
  const { error: memberError } = await supabase
    .from('organization_members')
    .insert({
      organization_id: invitation.organization_id,
      user_id: userId,
      role: invitation.role,
      permissions: invitation.permissions,
      client_access: invitation.client_access,
      invited_by: invitation.invited_by,
    });

  if (memberError) throw memberError;

  // Mark invitation as accepted
  const { error: updateError } = await supabase
    .from('organization_invitations')
    .update({ accepted_at: new Date().toISOString() })
    .eq('id', invitation.id);

  if (updateError) throw updateError;

  // Mark user as onboarding complete (invited users skip onboarding)
  // They're joining an existing organization, don't need to set up a company
  const { error: metadataError } = await supabase.auth.updateUser({
    data: {
      onboarding_completed: true,
      joined_via_invitation: true,
      invitation_organization_id: invitation.organization_id,
    },
  });

  if (metadataError) {
    logger.error('Failed to update user metadata after invitation acceptance:', metadataError);
    // Don't throw - invitation is already accepted, this is just metadata
  }
}

export async function getInvitationByToken(
  token: string
): Promise<OrganizationInvitation | null> {
  const { data, error } = await supabase
    .from('organization_invitations')
    .select(`
      *,
      organization:organization_id (
        name
      )
    `)
    .eq('token', token)
    .is('accepted_at', null)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return {
    ...data,
    organization_name: data.organization?.name,
    is_valid: new Date(data.expires_at) > new Date(),
  } as any;
}

// ============================================================================
// EMAIL OPERATIONS
// ============================================================================

async function sendInvitationEmail(invitation: OrganizationInvitation): Promise<void> {
  try {
    // Get organization name for email
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', invitation.organization_id)
      .single();

    if (orgError) throw orgError;

    // Get current user (inviter) email
    const { data: { user: currentUser } } = await supabase.auth.getUser();

    // Call Supabase Edge Function to send email
    const response = await api.sendTeamInvitation({
      email: invitation.email,
      token: invitation.token,
      organizationName: org?.name || 'Unknown Organization',
      role: invitation.role,
      invitedByEmail: currentUser?.email || 'Team Member',
    });

    logger.info('Invitation email sent successfully:', {
      email: invitation.email,
      emailId: response?.emailId,
    });
  } catch (error: any) {
    logger.error('Error sending invitation email:', error);
    // Don't throw - allow invitation to be created even if email fails
    // The user can resend later
  }
}

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

export function getDefaultPermissionsForRole(
  role: keyof typeof ROLE_PERMISSIONS
): MemberPermissions {
  return { ...ROLE_PERMISSIONS[role] };
}

export async function hasPermission(
  organizationId: string,
  userId: string,
  permission: keyof MemberPermissions
): Promise<boolean> {
  const member = await getCurrentUserMembership(organizationId, userId);
  if (!member) return false;

  return member.permissions[permission];
}

// ============================================================================
// ANALYTICS & STATS
// ============================================================================

export async function getOrganizationStats(organizationId: string) {
  const [members, invitations, clients] = await Promise.all([
    getOrganizationMembers(organizationId),
    getOrganizationInvitations(organizationId),
    supabase
      .from('applicant_profiles')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId),
  ]);

  return {
    totalMembers: members.length,
    adminCount: members.filter(m => m.role === 'admin').length,
    editorCount: members.filter(m => m.role === 'editor').length,
    viewerCount: members.filter(m => m.role === 'viewer').length,
    pendingInvitations: invitations.length,
    totalClients: clients.count || 0,
  };
}
